import { Form, InputGroup } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import Label from './Label';
import { Validator } from './ValidationResult';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeLabel, validateOptions } from '../utils/generalUtils';
import { readField } from '../utils/objectUtilities';
import { safeEval } from '../utils/safeEval';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import { materializeValue } from '../utils/lazyValue';

export const customCtrlDef = {
    toString: async ({ value, args, defaultValue, def }) => {
        value = await materializeValue(value);
        if (value === null || value === undefined) {
            return defaultValue;
        }
        let options = def.options || [];
        if (typeof options === 'string') {
            options = safeEval(options)(value, args);
        } else if (typeof options === 'function') {
            options = options(value, args);
        }
        const hasObjectEntries = options.some(o => typeof o === 'object');
        let labelField = def.key;
        let valueField = def.value;
        if (hasObjectEntries && !labelField) {
            labelField = 'label';
        }
        if (hasObjectEntries && !valueField) {
            valueField = 'value';
        }
        validateOptions(options, labelField, valueField);
        return !valueField ? value : (readField(options.find(o => readField(o, valueField) == value), labelField) ?? '');
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "table", field: "options", sort: [], columns: [
                        {title: t('label'), type: "text", field: "label", editable: true, required: true},
                        { title: "Value", type: "text", field: "value", editable: true, required: true }
                    ]}
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ value, def, href, ...props }, { args }) => {
        let options = def.options || [];
        if (typeof options === 'string') {
            options = safeEval(options)(value, args);
        } else if (typeof options === 'function') {
            options = options(value, args);
        }
        const hasObjectEntries = options.some(o => typeof o === 'object');
        let labelField = def.key;
        let valueField = def.value;
        if (hasObjectEntries && !labelField) {
            labelField = 'label';
        }
        if (hasObjectEntries && !valueField) {
            valueField = 'value';
        }
        validateOptions(options, labelField, valueField);
        return <Select value={value} wildcard={def.wildcard} options={options} labelField={labelField} valueField={valueField} href={href ?? def.href} {...props}></Select>
    }
}

/**
 * A simple wrapper for a dropdown list (eg HTML select) control
 * @component
 */
const Select = withComponentCapabilities(({ hideLabel = false, placeholder, wildcard, value: inputValue, required = false, prependCtrl, prependText, appendCtrl, appendText, disabled = false, readonly = false, onChange, onBlur, options = [], labelField, valueField, selectableField, href, ...props}) => {
    const { t } = useTranslation('common');
    const [value, setValue] = useState(inputValue)
    const [validity, setValidity] = useState();
    const ctrlRef = useRef();

    useEffect(() => {
        setValue(inputValue);
        setValidity();
    }, [inputValue]);

    if (prependCtrl && prependText) {
        throw new Error("Cannot specify both prependCtrl and prependText");
    }

    if (appendCtrl && appendText) {
        throw new Error("Cannot specify both appendCtrl and appendText");
    }

    validateOptions(options, labelField, valueField);
    
    const placeholderOption = useMemo(() => {
        return options.find(option => {
            const v = valueField ? option[valueField] : option;
            return v === null || v === undefined || v === '';
        });
    }, [options, valueField]);

    const label = useMemo(() => {
        if (value !== undefined && value !== '' && value !== null) {
            if (labelField) {
                let option = options.find(opt => opt[valueField] == value);
                return option ? option[labelField] : value;
            }
            return value;
        } else if (placeholderOption) {
            return labelField ? placeholderOption[labelField] : placeholderOption;
        }
        return wildcard
    }, [value, placeholderOption, wildcard, labelField, options, valueField]);

    const handleChange = e => {
        e.stopPropagation(); 
        e.preventDefault();
        setValue(e.target.value);
        ctrlRef.current.setCustomValidity('');
        let valid = ctrlRef.current.checkValidity();
        if (valid && required) {
            if (!e.target.value) {
                valid = false;
                ctrlRef.current.setCustomValidity(t('validationRequired'))
                ctrlRef.current.checkValidity();
            }
        }
        setValidity(valid)
        if (valid) {
            onChange(e.target.value)
        }
    }

    const handleBlur = (...args) => {
        setValidity(ctrlRef.current.checkValidity())
        onBlur?.(...args)
    }

    let prepend = null;
    if (prependCtrl || prependText) {
        prepend = prependCtrl ?? <InputGroup.Text>{prependText}</InputGroup.Text>
    }

    let append = null;
    if (appendCtrl || appendText) {
        append = appendCtrl ?? <InputGroup.Text>{appendText}</InputGroup.Text>
    }
    
    const select = <Form.Control ref={ctrlRef} as="select" isInvalid={validity === false} disabled={disabled} {...props} onChange={handleChange} value={value ?? ''} required={required} onBlur={handleBlur} onInvalid={() => setValidity(false)}>
        { !wildcard && !placeholderOption ? <option style={{display:'none'}} value="">{makeLabel(placeholder, required)}</option> : null}
        { wildcard ? <option value="">{wildcard}</option> : null}
        { options.map(opt => <option style={{'display': (selectableField && !opt[selectableField]) ? 'none' : 'block'}} key={valueField ? opt[valueField] : opt} value={valueField ? opt[valueField] : opt}>{labelField ? opt[labelField] : opt}</option>) }
    </Form.Control>
    
    return readonly ?
            <Label hideLabel={hideLabel} placeholder={placeholder} required={required} text={label} href={href}></Label> : 
            <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={wildcard || value || placeholderOption} href={href}>
                {prepend || append ?
                    <InputGroup>
                        <Validator ref={ctrlRef} value={inputValue} onValidation={setValidity}>
                                        {prepend && <InputGroup.Prepend>{prepend}</InputGroup.Prepend>}
                                        {select}
                                        {append && <InputGroup.Append>{append}</InputGroup.Append>}
                        </Validator>
                    </InputGroup>
                : <Validator ref={ctrlRef} value={inputValue} onValidation={setValidity}>
                    {select}
                </Validator>}
            </FloatingLabel>
}, {

});

Select.propTypes = {
    /**
     * The value to be displayed by the control
     */
    value: PropTypes.any,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * Function to call when the control looses focus
     */
    onBlur: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * When provided indicates the dropdown should allow a null selection which is to be treated as a wildcard.
     * The string supplied is used as the label of the wildcard entry (standard use would be something like
     * '(any)' or '(all)' depending on the context)
     */
    wildcard: PropTypes.string,

    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,

    /**
     * List of selection choices to appear in the dropdown
     */
    options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,

    /**
     * Field to extract the label for each option - required if options is an array of objects
     */
    labelField: PropTypes.string,

    /**
     * Field to extract the value for each option - required if options is an array of objects
     */
    valueField: PropTypes.string,

     /**
     * The field to check if an option be rendered 
     */
     selectableField: PropTypes.string,

    /**
     * A control to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependCtrl: PropTypes.node,

    /**
     * Text or icon to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependText: PropTypes.node,

    /**
     * A control to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
     */
    appendCtrl: PropTypes.node,

    /**
    * Text or icon to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
    */
    appendText: PropTypes.node,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default Select;